export interface StoredAdminSession {
  accessToken: string;
  expiresAt: string;
  operator: { username: string };
}

const STORAGE_KEY = 'tichchi.admin.session';

/** Absent in tests and any non-browser context. */
function defaultStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.localStorage;
}

function isStoredSession(value: unknown): value is StoredAdminSession {
  const candidate = value as Partial<StoredAdminSession> | null;
  return (
    !!candidate &&
    typeof candidate.accessToken === 'string' &&
    typeof candidate.expiresAt === 'string' &&
    typeof candidate.operator?.username === 'string'
  );
}

export function readStoredSession(
  storage: Storage | null = defaultStorage(),
): StoredAdminSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredSession(
  session: StoredAdminSession,
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // A full or unavailable storage must not block sign-in; the session simply
    // does not survive a reload.
  }
}

export function clearStoredSession(
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing actionable; the caller still signs out of Firebase.
  }
}

export function isSessionExpired(
  session: Pick<StoredAdminSession, 'expiresAt'>,
  now = Date.now(),
): boolean {
  const expiresAt = Date.parse(session.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}
